import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'

import { CodeEditor } from '../../../components/CodeEditor'
import { showToast } from '../../../components/Toast/showToast'

export type SmrtArtifactRow = {
  id: string
  smrtVersion: string
}

type SmrtArtifactsColumnsHandlers = {
  onView: (smrtVersion: string) => void
}

export const useSmrtArtifactsAdminColumns = ({
  onView,
}: SmrtArtifactsColumnsHandlers) =>
  useMemo<ColumnDef<SmrtArtifactRow>[]>(
    () => [
      {
        accessorKey: 'smrtVersion',
        header: 'Version',
        meta: {
          toText: val => val as string,
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: info => {
          const a = info.row.original
          return (
            <Flex gap="2">
              <Button
                size="xs"
                variant="outline"
                onClick={e => {
                  e.stopPropagation()
                  onView(a.smrtVersion)
                }}
                data-testid={`view-artifact-${a.smrtVersion}`}
              >
                View
              </Button>
            </Flex>
          )
        },
        meta: {
          toText: () => '',
        },
      },
    ],
    [onView],
  )

interface SmrtArtifactViewModalProps {
  isOpen: boolean
  onClose: () => void
  loadingFull: boolean
  viewJson: string
  smrtVersion: string | null
}

export const SmrtArtifactViewModal = ({
  isOpen,
  onClose,
  loadingFull,
  viewJson,
  smrtVersion,
}: SmrtArtifactViewModalProps) => (
  <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
    <ModalOverlay />
    <ModalContent>
      <ModalHeader>
        SMRT artifact {smrtVersion ? `(v${smrtVersion})` : ''}
      </ModalHeader>
      <ModalBody>
        {loadingFull ? (
          <Text>Loading...</Text>
        ) : (
          <CodeEditor
            value={viewJson}
            readOnly
            height="70vh"
            language="json"
            data-testid="artifact-json-content"
          />
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button
          size="sm"
          onClick={() => {
            if (viewJson) {
              void navigator.clipboard.writeText(viewJson)
              showToast({
                title: 'Copied to clipboard',
                variant: 'success',
              })
            }
          }}
        >
          Copy JSON
        </Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
)
